module Sm.UI.Components.TextEdit

open Browser.Types
open Fable.React
open Fable.React.Props
open MobF.React

open Sm.UI.Form
open Sm.UI.Form.TextInput

type ValidationDisplay =
    | Subtle
    | Verbose

type Props = {
    Input: TextInput.Type
    ShowControls: bool
    ValidationDisplay: ValidationDisplay
}

[<ObserverComponent>]
let TextEdit (props: Props) =
    let m = props.Input
    let s = m.State

    let submit _ =
        m.Post Submit

    let cancel _ =
        m.Post Cancel

    let onRef e =
        let inline handle (element: HTMLInputElement) =
            if not (element.matches ":focus") then
                if s.Config.FocusBehavior.IsSome then
                    element.focus()

                match s.Config.InitialValue with
                | Some value ->
                    match s.Config.FocusBehavior with
                    | None | Some BrowserDefault -> ()
                    | Some SelectAll -> element.select()
                    | Some MoveToEnd -> element.setSelectionRange(value.Length, value.Length)
                | None -> element.value <- ""

        match e, s.EditValue with
        | HTMLInputElement e, None -> handle e
        | HTMLTextAreaElement e, None -> handle (e :?> HTMLInputElement)
        | _ -> ()

    let hint = s.Config.Hint
    let initial = s.Config.InitialValue
    let validation = m.Computed.ValidationResult

    let invalid = match validation with Ok _ -> false | _ -> true
    let hasEdit = s.EditValue.IsSome
    let editIsInvalid = hasEdit && invalid

    let value =
        match s.EditValue, initial with
        | Some edit, _ -> edit
        | None, Some initial -> initial
        | None, None -> ""

    let classList = renderClassList [
        Append "text-edit"
        Toggle ("is-autoexpand", s.Config.Autoexpand)
        Toggle ("is-multiline", s.Config.Multiline)
        Toggle ("is-singleline", not s.Config.Multiline)
        Toggle ("is-invalid", invalid)

        yield! m.State.Config.Classes |> List.map Append
    ]

    div [Class classList; if s.Config.Autoexpand then Data ("value", value)] [
        match hasEdit, validation with
        | true, Error message ->
            em [Class "validation"; Title message] [
                iconFor Warning [Class "warning-icon"]
            ]
        | _ -> nothing

        textarea [
            Ref onRef
            Class (renderClassList [
                Toggle ("invalid", editIsInvalid)
            ])
            Style [
                match s.Config.LayoutHint with
                | None -> ()
                | Some (SuggestWidth width) -> Width width
                | Some (SuggestHeight height) -> Height height
                | Some (SuggestSize (width, height)) ->
                    Width width
                    Height height
            ]
            Title hint
            Placeholder hint
            SpellCheck false
            Value value
            if s.Config.Autoexpand then Rows 1
            OnKeyDown (fun e ->
                match e.key with
                | "Enter" -> 
                    submit ()
                    e.preventDefault()
                | "Esc" | "Escape" -> 
                    cancel ()
                    e.preventDefault()
                | _ -> ()
            )
            OnInput (fun e ->
                m.Post (Edit e.Value)
            )
        ] []

        if props.ShowControls then
            div [Class "controls"] [
                button [OnClick cancel; Data ("action", "cancel")] [str "Cancel"]
                button [OnClick submit; Disabled invalid; Data ("action", "accept")] [str "Ok"]
            ]

        match props.ValidationDisplay, validation with
        | Subtle, _ -> nothing
        | _, Ok _ -> nothing
        | Verbose, Error message -> aside [Class "validation"] [str message]
    ]
